import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { VFC } from "react";
import styled from "styled-components";
import { Markdown } from "components";
import { PipelineRoutingLinkButton } from "components/Molecules/PipelineRoutingLinkButton";
import { FullArrow } from "icons";
import { Hero13Content } from "types/graphql/generated";

interface Props {
  content: Hero13Content;
}

const Hero13: VFC<Props> = withErrorBoundary(
  ({ content: { title, description, hero13Label, hero13Link } }) => (
    <StyledHero13 className="Content">
      <div className="Hero13__container">
        <Markdown className="Hero13__title Color_eucalyptus80 Font_80_88_600 lg:Font_64_72_600 md:Font_48_54_600">
          {title}
        </Markdown>
        <Markdown className="Hero13__description Color_secondary Font_24_32_450">{description}</Markdown>
        <PipelineRoutingLinkButton href={hero13Link} rightIcon={<FullArrow />} className="Hero13__button">
          {hero13Label}
        </PipelineRoutingLinkButton>
      </div>
    </StyledHero13>
  ),
  {}
);

const StyledHero13 = styled.section`
  .Hero13__container {
    display: flex;
    flex-direction: column;
    text-align: center;
    align-items: center;
    padding-top: 136px;
    padding-bottom: 96px;
    background: radial-gradient(
        52.26% 107.35% at 50% 149.16%,
        rgb(255 205 133 / 75%) 54.92%,
        rgb(246 243 241 / 0%) 100%
      ),
      linear-gradient(180deg, #e0eeea 50%, #c0ddd6 100%);
  }

  .Hero13__title {
    max-width: 800px;
  }

  .hero13__checkbox {
    width: 72px;
    height: 72px;
    display: inline-block;
    border-radius: 16px;
    color: ${({ theme }) => theme.colors.coral["80"]};
    box-shadow: 0 48px 40px -8px rgb(20 20 20 / 12%);
    background: linear-gradient(180deg, #ffd18f 0%, #ff9f7b 100%);
    position: relative;
    margin-bottom: -12px;
  }

  .hero13__checkbox::before {
    content: "";
    position: absolute;
    width: 6px;
    top: 50%;
    left: 28%;
    height: 20%;
    background-color: ${({ theme }) => theme.colors.coral["80"]};
    transform: translateX(12px) rotate(-45deg);
    transform-origin: left bottom;
    border-radius: 0 0 6px;
    z-index: 1;
  }

  .hero13__checkbox::after {
    content: "";
    position: absolute;
    bottom: 22.7px;
    left: 29%;
    height: 6px;
    width: 46%;
    background-color: ${({ theme }) => theme.colors.coral["80"]};
    transform: translateX(12px) rotate(-45deg);
    transform-origin: left bottom;
    border-radius: 6px 0 0;
  }

  .Hero13__description {
    max-width: 800px;
    margin-top: 24px;
  }

  .Hero13__button {
    margin-top: 40px;
    padding-left: 32px;
    padding-right: 32px;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.xl}px`}) {
    .Hero13__container {
      padding-bottom: 96px;
    }

    .hero13__checkbox::before {
      width: 6px;
      top: 52%;
      left: 30%;
      height: 20%;
      transform: translateX(12px) rotate(-49deg);
    }

    .hero13__checkbox::after {
      bottom: 20.7px;
      left: 30.5%;
      height: 6px;
      width: 44%;
      transform: translateX(12px) rotate(-51deg);
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    &.Content {
      padding-left: 0;
      padding-right: 0;
    }

    .Hero13__container {
      padding-left: 32px;
      padding-right: 32px;
    }

    .Hero13__title {
      max-width: 660px;
    }

    .hero13__checkbox {
      width: 56px;
      height: 54px;
      margin-bottom: -6px;
      margin-left: 6px;
    }

    .hero13__checkbox::before {
      width: 4px;
      top: 52%;
      left: 20%;
      height: 20%;
    }

    .hero13__checkbox::after {
      bottom: 15.8px;
      left: 22.3%;
      height: 5px;
      width: 41.5%;
      transform: translateX(12px) rotate(-52deg);
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    .Hero13__container {
      padding: 64px 24px;
      align-items: flex-start;
      text-align: left;
    }

    .hero13__checkbox {
      width: 38px;
      height: 39px;
      border-radius: 10px;
      margin-bottom: -6px;
      margin-left: 6px;
    }

    .hero13__checkbox::before {
      width: 3px;
      top: 53.4%;
      left: 15%;
    }

    .hero13__checkbox::after {
      bottom: 10.3px;
      left: 16.4%;
      height: 3px;
      width: 45%;
      transform: translateX(12px) rotate(-52deg);
    }

    .Hero13__button {
      margin-top: 40px;
      padding-left: 24px;
      padding-right: 24px;
    }
  }
`;

gql`
  fragment Hero13Content on ComponentHeroHero13 {
    title
    description
    hero13Label
    hero13Link
  }
`;

export { Hero13 };
export type { Props as Hero13Props };
